import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { leaguesAPI, sportsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Trophy, Users, MapPin, Search, Plus, Lock, Globe, Filter
} from 'lucide-react';
import { resolveMediaUrl } from '@/lib/utils';

export const LeaguesPage = () => {
  const [loading, setLoading] = useState(true);
  const [leagues, setLeagues] = useState([]);
  const [myLeagues, setMyLeagues] = useState([]);
  const [sports, setSports] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [showPublicOnly, setShowPublicOnly] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leaguesRes, myLeaguesRes, sportsRes] = await Promise.all([
          leaguesAPI.getAll(),
          leaguesAPI.getMyLeagues(),
          sportsAPI.getAll()
        ]);
        setLeagues(leaguesRes.data);
        setMyLeagues(myLeaguesRes.data);
        setSports(sportsRes.data);
      } catch (error) {
        console.error('Error fetching leagues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredLeagues = leagues.filter(league => {
    const matchesSearch = league.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          league.city.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSport = selectedSport === 'all' || league.sport_id === selectedSport;
    const matchesVisibility = !showPublicOnly || league.is_public;
    
    return matchesSearch && matchesSport && matchesVisibility;
  });

  const isMyLeague = (leagueId) => {
    return myLeagues.some(l => l.league_id === leagueId);
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-12 w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="leagues-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Leghe</h1>
          <p className="text-muted-foreground">Scopri e unisciti alle leghe amatoriali</p>
        </div>
        <Link to="/leagues/new">
          <Button data-testid="create-league-btn">
            <Plus className="h-4 w-4 mr-2" />
            Crea Lega
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per nome o città..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-leagues"
          />
        </div>
        <Select value={selectedSport} onValueChange={setSelectedSport}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="sport-filter">
            <SelectValue placeholder="Sport" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli sport</SelectItem>
            {sports.map(sport => (
              <SelectItem key={sport.sport_id} value={sport.sport_id}>
                {sport.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={showPublicOnly ? "default" : "outline"}
          onClick={() => setShowPublicOnly(!showPublicOnly)}
          className="shrink-0"
        >
          <Globe className="h-4 w-4 mr-2" />
          Solo pubbliche
        </Button>
      </div>

      {/* My Leagues Section */}
      {myLeagues.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Le Mie Leghe
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {myLeagues.map((league) => (
              <LeagueCard key={league.league_id} league={league} isMember={true} />
            ))}
          </div>
        </div>
      )}

      {/* All Leagues */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold">
          {myLeagues.length > 0 ? 'Altre Leghe' : 'Tutte le Leghe'}
          <span className="text-muted-foreground font-normal text-sm ml-2">
            ({filteredLeagues.filter(l => !isMyLeague(l.league_id)).length})
          </span>
        </h2>
        
        {filteredLeagues.filter(l => !isMyLeague(l.league_id)).length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchQuery || selectedSport !== 'all' 
                  ? 'Nessuna lega trovata con questi filtri'
                  : 'Nessuna lega disponibile'}
              </p>
              <Link to="/leagues/new">
                <Button>Crea la prima lega</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredLeagues
              .filter(l => !isMyLeague(l.league_id))
              .map((league) => (
                <LeagueCard key={league.league_id} league={league} isMember={false} />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

const LeagueCard = ({ league, isMember }) => {
  return (
    <Link to={`/leagues/${league.league_id}`}>
      <Card className="card-interactive h-full overflow-hidden" data-testid={`league-card-${league.league_id}`}>
        {/* Cover Image */}
        <div className="h-32 bg-muted relative overflow-hidden">
          {league.cover_image_url ? (
            <img 
              src={resolveMediaUrl(league.cover_image_url)} 
              alt={league.name}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-primary/10">
              <Trophy className="h-12 w-12 text-primary/50" />
            </div>
          )}
          <div className="absolute top-2 right-2">
            <Badge variant={league.is_public ? "secondary" : "outline"} className="bg-background/80 backdrop-blur">
              {league.is_public ? (
                <><Globe className="h-3 w-3 mr-1" /> Pubblica</>
              ) : (
                <><Lock className="h-3 w-3 mr-1" /> Privata</>
              )}
            </Badge>
          </div>
          {isMember && (
            <div className="absolute top-2 left-2">
              <Badge className="bg-primary">Membro</Badge>
            </div>
          )}
        </div>

        <CardContent className="p-4 space-y-3">
          <div>
            <h3 className="font-semibold text-lg truncate">{league.name}</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="secondary" className="text-xs">
                {league.sport_name}
              </Badge>
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {league.city}
              </span>
            </div>
          </div>

          {league.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {league.description}
            </p>
          )}

          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1 text-muted-foreground">
              <Users className="h-4 w-4" />
              {league.members_count || 0} membri
            </span>
            {isMember && league.user_role && (
              <Badge variant="outline" className="text-xs capitalize">
                {league.user_role}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default LeaguesPage;
